/**
 * How the Mod Maker *presents* the field vocabulary — labels a person can
 * read, grouped the way a person thinks, with the engine's own names kept
 * one layer down (`data-field`, the recipe JSON, and the docs).
 *
 * `mods/fields.ts` stays the single source of wire truth; this module only
 * decides label, grouping, help text and control type. A field that appears
 * in neither a section list nor ADVANCED simply isn't shown — the one
 * deliberate case is the item header's `soundId`, which the engine reads
 * only for *containers* (sfxBuildOpenName, game_sound.cc:1488): showing a
 * "sound" knob that does nothing on a weapon would be exactly the kind of
 * mystery code this pass removes.
 */

import { CALIBER_TYPES, DAMAGE_TYPES, MATERIAL_TYPES, type ItemTypeName } from '../mods/fields';

/** What kind of input a field gets. */
export type ControlKind =
  | { kind: 'number' }
  | { kind: 'enum'; labels: readonly string[] }
  | { kind: 'itemPid'; itemType: ItemTypeName }
  | { kind: 'perk' }
  | { kind: 'weaponSound' }
  | { kind: 'projectile' }
  /**
   * A picture out of one art list, chosen by looking at it. `list` is the
   * `gArtListDescriptions` entry (art.cc:79) the field's object-type nibble
   * selects: object type 0 is `items`, 7 is `inven`. `allowNone` is whether
   * -1 ("no art in this slot") is a legal value for the field.
   */
  | { kind: 'artFid'; list: 'items' | 'inven'; allowNone: boolean }
  | { kind: 'weaponAnim' }
  | { kind: 'critterBase' };

export interface FieldPresentation {
  label: string;
  /** One human sentence, shown under the input. Empty string = no line. */
  help: string;
  control: ControlKind;
}

/** Display names for CaliberType, index-aligned with CALIBER_TYPES. */
export const CALIBER_LABELS: readonly string[] = [
  'None (no ammo)',
  'Rocket',
  'Flamethrower fuel',
  'Small energy cell',
  'Micro fusion cell',
  '.223',
  '5mm',
  '.40 cal',
  '10mm',
  '.44 Magnum',
  '14mm',
  '12 gauge',
  '9mm',
  'BB',
  '.45',
  '2mm',
  '4.7mm caseless',
  'HN needler',
  '7.62mm',
];

/** Display names for DamageType, index-aligned with DAMAGE_TYPES. */
export const DAMAGE_LABELS: readonly string[] = ['Normal', 'Laser', 'Fire', 'Plasma', 'Electrical', 'EMP', 'Explosion'];

/** Display names for MaterialType, index-aligned with MATERIAL_TYPES. */
export const MATERIAL_LABELS: readonly string[] = ['Glass', 'Metal', 'Plastic', 'Wood', 'Dirt', 'Stone', 'Cement', 'Leather'];

/**
 * The seven critical-failure tables (`_cf_table`, combat.cc:1887), named by
 * the weapon class vanilla assigns them and summarised by their worst rows.
 * The table only comes up on a critically failed attack roll.
 */
export const CF_TABLE_LABELS: readonly string[] = [
  'Unarmed — worst: hurt yourself, knocked down, crippled limb',
  'Melee — worst: drop the weapon, hit yourself',
  'Guns — worst: lose the ammo, weapon breaks',
  'Energy weapons — worst: weapon explodes, lose a turn',
  'Grenades — worst: dud, or goes off in your hand',
  'Rockets — worst: weapon destroyed, explosion',
  'Flamers — worst: explosion, catch fire',
];

// Sanity: the label tables must track the wire tables.
void (CALIBER_TYPES.length === CALIBER_LABELS.length &&
  DAMAGE_TYPES.length === DAMAGE_LABELS.length &&
  MATERIAL_TYPES.length === MATERIAL_LABELS.length);

const NUMBER: ControlKind = { kind: 'number' };

const PRESENTATION: Record<string, FieldPresentation> = {
  // --- damage & attack (weapon) ------------------------------------------
  minDamage: { label: 'Damage, minimum', help: 'Least damage one hit can do.', control: NUMBER },
  maxDamage: { label: 'Damage, maximum', help: 'Most damage one hit can do.', control: NUMBER },
  damageType: { label: 'Damage type', help: 'Which resistances the target gets to use.', control: { kind: 'enum', labels: DAMAGE_LABELS } },
  maxRange1: { label: 'Range — primary attack', help: 'In hexes. A hex is roughly a metre.', control: NUMBER },
  maxRange2: { label: 'Range — secondary attack', help: '0 when the weapon has no second mode.', control: NUMBER },
  actionPointCost1: { label: 'AP cost — primary attack', help: 'Action points one attack spends.', control: NUMBER },
  actionPointCost2: { label: 'AP cost — secondary attack', help: '', control: NUMBER },
  rounds: { label: 'Bullets per burst', help: '0 for weapons that cannot burst.', control: NUMBER },
  minStrength: { label: 'Strength required', help: 'Below this ST the wielder shoots worse.', control: NUMBER },

  // --- ammo (weapon) ------------------------------------------------------
  caliber: { label: 'Ammo caliber', help: 'What kind of ammunition it chambers.', control: { kind: 'enum', labels: CALIBER_LABELS } },
  ammoTypePid: { label: 'Ammo it comes loaded with', help: '', control: { kind: 'itemPid', itemType: 'ammo' } },
  ammoCapacity: { label: 'Magazine size', help: 'Shots per reload.', control: NUMBER },

  // --- character feel -----------------------------------------------------
  soundCode: { label: 'Firing sound', help: 'Borrowed from another weapon — press play to hear it.', control: { kind: 'weaponSound' } },
  // The control renders its own explanation — the mechanics differ by slot
  // (weapon-class perk vs while-worn stat bonus; modmaker-perks.ts).
  perk: { label: 'Special property (perk)', help: '', control: { kind: 'perk' } },

  // --- appearance ---------------------------------------------------------
  // Every one of these is an index into a list the player's own install
  // declares; the recipe carries no art, only which existing picture to point
  // at. An index that names nothing draws nothing (art.cc:427-430).
  fid: { label: 'Look on the ground', help: 'The picture it shows lying on the floor.', control: { kind: 'artFid', list: 'items', allowNone: false } },
  inventoryFid: {
    label: 'Look in the inventory',
    help: 'The picture in the inventory and on the barter table.',
    control: { kind: 'artFid', list: 'inven', allowNone: true },
  },
  animationCode: {
    label: 'Held like a…',
    help: 'The character’s stance and attack animations. If their body model has no frames for it, the game refuses to equip the weapon.',
    control: { kind: 'weaponAnim' },
  },
  maleFid: {
    label: 'Worn look — male',
    help: 'Replaces the wearer’s whole body model while worn; none keeps their own look.',
    control: { kind: 'critterBase' },
  },
  femaleFid: { label: 'Worn look — female', help: '', control: { kind: 'critterBase' } },

  // --- protection (armor) -------------------------------------------------
  armorClass: { label: 'Armor Class', help: 'Makes the wearer harder to hit at all.', control: NUMBER },

  // --- carry & trade ------------------------------------------------------
  weight: { label: 'Weight (lbs)', help: 'Negative is allowed — the item lightens whoever carries it.', control: NUMBER },
  size: { label: 'Inventory size', help: 'How much bag space it takes.', control: NUMBER },
  cost: { label: 'Base price ($)', help: 'What traders think it is worth before barter skill.', control: NUMBER },

  // --- advanced -----------------------------------------------------------
  material: { label: 'Material', help: 'Drives break/burn messages and hit sounds.', control: { kind: 'enum', labels: MATERIAL_LABELS } },
  lightDistance: { label: 'Light radius (hexes)', help: 'How far the item itself glows.', control: NUMBER },
  lightIntensity: { label: 'Light strength', help: '0 to 65536.', control: NUMBER },
  criticalFailureType: {
    label: 'When an attack critically fails…',
    help: 'Rolled only on a critical miss.',
    control: { kind: 'enum', labels: CF_TABLE_LABELS },
  },
  projectilePid: {
    label: 'Projectile',
    help: 'What visibly flies to the target. None means an instant hit — most bullets.',
    control: { kind: 'projectile' },
  },
};

for (const [i, name] of DAMAGE_TYPES.entries()) {
  PRESENTATION[`damageResistance.${name}`] = {
    label: `${DAMAGE_LABELS[i]} resistance %`,
    help: '',
    control: NUMBER,
  };
  PRESENTATION[`damageThreshold.${name}`] = {
    label: `${DAMAGE_LABELS[i]} threshold`,
    help: '',
    control: NUMBER,
  };
}
PRESENTATION['damageResistance.normal']!.help = 'Percent of that damage shrugged off, after the threshold.';
PRESENTATION['damageThreshold.normal']!.help = 'Flat points of that damage ignored before resistance.';

/** Presentation for one field. Unlisted fields fall back to the raw name. */
export function presentField(name: string): FieldPresentation {
  return PRESENTATION[name] ?? { label: name, help: '', control: NUMBER };
}

export interface FieldSection {
  title: string;
  fields: string[];
}

const WEAPON_SECTIONS: FieldSection[] = [
  { title: 'Damage', fields: ['minDamage', 'maxDamage', 'damageType'] },
  { title: 'Appearance', fields: ['fid', 'inventoryFid', 'animationCode'] },
  {
    title: 'Attacks',
    fields: ['maxRange1', 'actionPointCost1', 'maxRange2', 'actionPointCost2', 'rounds', 'minStrength', 'criticalFailureType'],
  },
  { title: 'Ammo', fields: ['caliber', 'ammoTypePid', 'ammoCapacity'] },
  { title: 'Feel', fields: ['soundCode', 'projectilePid', 'perk'] },
  { title: 'Carry & trade', fields: ['weight', 'size', 'cost'] },
];

const ARMOR_SECTIONS: FieldSection[] = [
  { title: 'Protection', fields: ['armorClass'] },
  { title: 'Appearance', fields: ['fid', 'inventoryFid', 'maleFid', 'femaleFid'] },
  { title: 'Resistances', fields: DAMAGE_TYPES.map((n) => `damageResistance.${n}`) },
  { title: 'Damage thresholds', fields: DAMAGE_TYPES.map((n) => `damageThreshold.${n}`) },
  { title: 'Feel', fields: ['perk'] },
  { title: 'Carry & trade', fields: ['weight', 'size', 'cost'] },
];

/** The container-only `soundId` and art/script/identity fields never show. */
const NEVER_SHOWN = new Set(['soundId']);

/**
 * Groups an item type's editable fields: the curated sections first, then
 * whatever is left in a fold-away Advanced section. Types without a curated
 * layout (future: drugs, ammo, …) get everything in one "Fields" section.
 */
export function sectionsFor(type: ItemTypeName, editableNames: string[]): { sections: FieldSection[]; advanced: string[] } {
  const curated = type === 'weapon' ? WEAPON_SECTIONS : type === 'armor' ? ARMOR_SECTIONS : null;
  const names = editableNames.filter((n) => !NEVER_SHOWN.has(n));
  if (!curated) return { sections: [{ title: 'Fields', fields: names }], advanced: [] };

  const placed = new Set(curated.flatMap((s) => s.fields));
  const sections = curated
    .map((s) => ({ title: s.title, fields: s.fields.filter((f) => names.includes(f)) }))
    .filter((s) => s.fields.length);
  const advanced = names.filter((n) => !placed.has(n));
  return { sections, advanced };
}
